import React, { useEffect, useState } from 'react';
import { api } from '../api';
import FahrzeugForm from '../components/FahrzeugForm';
import { exportKundenFahrzeuge } from '../utils/pdfExport';

function KundenKartei({ kunde }) {
  if (!kunde) return null;
  const adresse = [kunde.strasse, [kunde.plz, kunde.ort].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  const ap = kunde.ansprechpartner || [];

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--gray-200)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--gray-600)' }}>Kundendaten</span>
      </div>
      <div style={{ padding: '16px 20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '8px 24px' }}>
        {adresse && (
          <div>
            <div style={{ fontSize: 11, color: 'var(--gray-400)', marginBottom: 2 }}>Adresse</div>
            <div style={{ fontSize: 13, color: 'var(--gray-700)' }}>{adresse}</div>
          </div>
        )}
        {kunde.telefon && (
          <div>
            <div style={{ fontSize: 11, color: 'var(--gray-400)', marginBottom: 2 }}>Telefon</div>
            <a href={`tel:${kunde.telefon}`} style={{ fontSize: 13, color: 'var(--primary)', fontWeight: 500 }}>{kunde.telefon}</a>
          </div>
        )}
        {kunde.email && (
          <div>
            <div style={{ fontSize: 11, color: 'var(--gray-400)', marginBottom: 2 }}>E-Mail</div>
            <a href={`mailto:${kunde.email}`} style={{ fontSize: 13, color: 'var(--primary)' }}>{kunde.email}</a>
          </div>
        )}
        {kunde.website && (
          <div>
            <div style={{ fontSize: 11, color: 'var(--gray-400)', marginBottom: 2 }}>Website</div>
            <div style={{ fontSize: 13, color: 'var(--gray-700)' }}>{kunde.website}</div>
          </div>
        )}
        {kunde.notizen && (
          <div style={{ gridColumn: '1 / -1' }}>
            <div style={{ fontSize: 11, color: 'var(--gray-400)', marginBottom: 2 }}>Notizen</div>
            <div style={{ fontSize: 13, color: 'var(--gray-700)', whiteSpace: 'pre-wrap' }}>{kunde.notizen}</div>
          </div>
        )}
      </div>

      {ap.length > 0 && (
        <>
          <div style={{ borderTop: '1px solid var(--gray-100)', padding: '10px 20px 6px' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '.5px' }}>
              Ansprechpartner
            </span>
          </div>
          <div style={{ padding: '0 20px 16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
            {ap.map(p => (
              <div key={p.id} style={{ padding: '10px 12px', border: '1px solid var(--gray-200)', borderRadius: 8, background: 'var(--gray-50)' }}>
                {p.funktion && <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.4px', color: 'var(--primary)', marginBottom: 3 }}>{p.funktion}</div>}
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 3 }}>{p.name}</div>
                {p.telefon && <a href={`tel:${p.telefon}`} style={{ display: 'block', fontSize: 12, color: 'var(--primary)' }}>{p.telefon}</a>}
                {p.mobil && <a href={`tel:${p.mobil}`} style={{ display: 'block', fontSize: 12, color: 'var(--primary)' }}>{p.mobil}</a>}
                {p.email && <a href={`mailto:${p.email}`} style={{ display: 'block', fontSize: 12, color: 'var(--primary)' }}>{p.email}</a>}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function formatDate(d) {
  if (!d) return '–';
  const [y, m, day] = d.split('-');
  return `${day}.${m}.${y}`;
}

function getDueBadge(dateStr) {
  if (!dateStr) return null;
  const today = new Date();
  const d = new Date(dateStr);
  const diff = Math.ceil((d - today) / 86400000);
  if (diff < 0) return <span className="badge badge-danger">Überfällig</span>;
  if (diff <= 30) return <span className="badge badge-warning">Bald fällig</span>;
  return null;
}

export default function KundenDetail({ navigate, kundenId, alleMode }) {
  const [kunde, setKunde] = useState(null);
  const [fahrzeuge, setFahrzeuge] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editFahrzeug, setEditFahrzeug] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      if (alleMode) {
        const all = await api.getFahrzeuge();
        setFahrzeuge(all);
      } else {
        const [k, f, aps] = await Promise.all([
          api.getKunde(kundenId),
          api.getFahrzeugeByKunde(kundenId),
          api.getAnsprechpartner(kundenId)
        ]);
        setKunde({ ...k, ansprechpartner: aps });
        setFahrzeuge(f);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [kundenId, alleMode]);

  const handleSave = async (data) => {
    const payload = { ...data, kunden_id: alleMode ? data.kunden_id : kundenId };
    if (editFahrzeug) {
      await api.updateFahrzeug(editFahrzeug.id, payload);
    } else {
      await api.createFahrzeug(payload);
    }
    setShowForm(false);
    setEditFahrzeug(null);
    load();
  };

  const handleDelete = async () => {
    await api.deleteFahrzeug(deleteId);
    setDeleteId(null);
    load();
  };

  const filtered = fahrzeuge.filter(f =>
    (f.kennzeichen || '').toLowerCase().includes(search.toLowerCase()) ||
    (f.marke || '').toLowerCase().includes(search.toLowerCase()) ||
    (f.modell || '').toLowerCase().includes(search.toLowerCase()) ||
    (f.kunden_name || '').toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="spinner">Lade…</div>;

  return (
    <div>
      {!alleMode && (
        <div className="breadcrumb">
          <span className="breadcrumb-link" onClick={() => navigate('kunden')}>Kunden</span>
          <span className="breadcrumb-sep">›</span>
          <span>{kunde?.name}</span>
        </div>
      )}

      {!alleMode && <KundenKartei kunde={kunde} />}

      <div className="page-header">
        <div>
          <div className="page-title">
            {alleMode ? 'Alle Fahrzeuge' : `Fahrzeuge von ${kunde?.name}`}
          </div>
          <div className="page-subtitle">{fahrzeuge.length} Fahrzeug(e)</div>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <div className="search-bar">
            <input
              className="form-input"
              placeholder="Suchen…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          {!alleMode && kunde && (
            <button className="btn btn-secondary" onClick={() => exportKundenFahrzeuge(kunde, fahrzeuge)}>
              📄 PDF Export
            </button>
          )}
          {!alleMode && (
            <button className="btn btn-primary" onClick={() => { setEditFahrzeug(null); setShowForm(true); }}>
              + Fahrzeug hinzufügen
            </button>
          )}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="card">
          <div className="card-body empty-state">
            <div className="empty-state-icon">🚗</div>
            <div className="empty-state-title">{search ? 'Keine Treffer' : 'Keine Fahrzeuge'}</div>
            <div className="empty-state-desc">
              {search ? 'Suchbegriff anpassen.' : 'Noch keine Fahrzeuge angelegt.'}
            </div>
          </div>
        </div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Kennzeichen</th>
                {alleMode && <th>Kunde</th>}
                <th>Marke / Modell</th>
                <th>Farbe</th>
                <th>km-Stand</th>
                <th>HU</th>
                <th>SP</th>
                <th>Inspektion</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(f => (
                <tr key={f.id}>
                  <td>
                    <strong
                      style={{ cursor: 'pointer', color: 'var(--primary)' }}
                      onClick={() => navigate('fahrzeug-detail', { fahrzeugId: f.id, kundenId: f.kunden_id })}
                    >
                      {f.kennzeichen}
                    </strong>
                  </td>
                  {alleMode && (
                    <td>
                      <span
                        style={{ cursor: 'pointer', color: 'var(--primary)' }}
                        onClick={() => navigate('kunde-detail', { kundenId: f.kunden_id })}
                      >
                        {f.kunden_name}
                      </span>
                    </td>
                  )}
                  <td>{[f.marke, f.modell].filter(Boolean).join(' ') || '–'}</td>
                  <td>{f.farbe || '–'}</td>
                  <td>{f.kilometerstand ? f.kilometerstand.toLocaleString('de-DE') + ' km' : '–'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                      {formatDate(f.hu_faelligkeit)}
                      {getDueBadge(f.hu_faelligkeit)}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                      {formatDate(f.sp_faelligkeit)}
                      {getDueBadge(f.sp_faelligkeit)}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                      {formatDate(f.naechste_inspektion_datum)}
                      {getDueBadge(f.naechste_inspektion_datum)}
                    </div>
                  </td>
                  <td>
                    <div className="td-actions">
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => navigate('fahrzeug-detail', { fahrzeugId: f.id, kundenId: f.kunden_id })}
                      >
                        Details
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => { setEditFahrzeug(f); setShowForm(true); }}
                      >
                        ✏️
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => setDeleteId(f.id)}
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <FahrzeugForm
          initial={editFahrzeug}
          kundenId={kundenId}
          alleMode={alleMode}
          onSave={handleSave}
          onClose={() => { setShowForm(false); setEditFahrzeug(null); }}
        />
      )}

      {deleteId && (
        <div className="modal-overlay" onClick={() => setDeleteId(null)}>
          <div className="modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Fahrzeug löschen</span>
              <button className="modal-close" onClick={() => setDeleteId(null)}>✕</button>
            </div>
            <div className="modal-body confirm-dialog">
              <p>Möchten Sie dieses Fahrzeug wirklich löschen?</p>
              <div className="confirm-actions">
                <button className="btn btn-secondary" onClick={() => setDeleteId(null)}>Abbrechen</button>
                <button className="btn btn-danger" onClick={handleDelete}>Löschen</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
